import React from 'react';
import { Link } from 'react-router-dom';
import BrandLogoMark from '../brand/BrandLogoMark.jsx';
import { 
  FileText, Layers, FileImage, 
  FileDown, Scissors, QrCode, 
  Youtube, Instagram, Zap,
  Mail, MapPin, ExternalLink
} from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerGroups = [
    {
      title: 'Convert PDF',
      items: [
        { label: 'Word to PDF', to: '/word-to-pdf' },
        { label: 'JPG to PDF', to: '/image-to-pdf' },
        { label: 'Excel to PDF', to: '/csv-excel-converter' },
        { label: 'PPT to PDF', to: '/universal-converter' },
        { label: 'PDF to Word', to: '/pdf-to-word' },
        { label: 'PDF to JPG', to: '/pdf-to-jpg' },
      ]
    },
    {
      title: 'Optimize PDF',
      items: [
        { label: 'Merge PDF', to: '/merge-pdf' },
        { label: 'Split PDF', to: '/split-pdf' },
        { label: 'Compress PDF', to: '/image-compressor' },
        { label: 'Repair PDF', to: '/universal-converter' },
      ]
    },
    {
      title: 'Utilities',
      items: [
        { label: 'QR Generator', to: '/qr-code-generator' },
        { label: 'QR Reader', to: '/qr-code-reader' },
        { label: 'Image to Text', to: '/image-to-text' },
        { label: 'YouTube Downloader', to: '/youtube-downloader' },
        { label: 'Instagram Downloader', to: '/instagram-downloader' },
      ]
    },
    {
      title: 'Legal & Help',
      items: [
        { label: 'About Us', to: '/about' },
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Terms of Use', to: '/terms' },
        { label: 'Contact Us', to: '/contact' },
      ]
    }
  ];

  return (
    <footer className="relative z-10 border-t border-stone-200 dark:border-slate-800 bg-white dark:bg-slate-900 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 lg:gap-8 mb-16">
          {/* Brand Identity */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-6 group">
              <BrandLogoMark size={32} className="group-hover:scale-110 transition-transform" />
              <span className="font-display text-2xl font-bold text-slate-900 dark:text-white">FreeTooly</span>
            </Link>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed max-w-xs">
              Every tool you need to work with files in one place. Fast, 100% free, and completely private browser-side processing.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://mail.google.com/mail/?view=cm&fs=1&to=supportfreetooly@gmail.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-brand-600 transition-all border border-stone-100 dark:border-slate-700 hover:border-brand-300 shadow-sm"
                aria-label="Send us an email"
              >
                <Mail size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Grouped Links */}
          {footerGroups.map((group) => (
            <div key={group.title}>
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-900 dark:text-white mb-6">
                {group.title}
              </h3>
              <ul className="flex flex-col gap-3.5">
                {group.items.map((item) => (
                  <li key={item.label}>
                    {item.external ? (
                      <a 
                        href={item.to} 
                        className="text-sm text-slate-500 dark:text-slate-400 hover:text-brand-600 transition-colors flex items-center gap-1.5"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.label}
                        <ExternalLink size={12} className="opacity-50" />
                      </a>
                    ) : (
                      <Link 
                        to={item.to} 
                        className="text-sm text-slate-500 dark:text-slate-400 hover:text-brand-600 transition-colors"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-stone-100 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center md:items-start gap-2">
            <p className="text-xs text-slate-500 font-medium">
              Made with <span className="text-red-500">♥</span> for a more private web.
            </p>
            <p className="text-xs text-slate-400 opacity-60">
              © {currentYear} FreeTooly. Online browser-side file utility platform.
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="px-3 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 flex items-center gap-2">
              <Zap size={12} className="text-yellow-500" />
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tighter">Fastest Processing</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
